import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useNutrition } from '../core/NutritionProvider';

export function MobileNutritionSummary() {
  const { metrics, isLoading } = useNutrition();

  if (isLoading || !metrics) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="space-y-3 animate-pulse">
            <div className="h-6 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded" />
            <div className="h-2 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const getNetCalorieStatus = (status: string) => {
    switch (status) {
      case 'surplus': return { variant: 'default' as const, label: 'Surplus', color: 'text-blue-600' };
      case 'deficit': return { variant: 'destructive' as const, label: 'Deficit', color: 'text-red-600' };
      default: return { variant: 'secondary' as const, label: 'Maintenance', color: 'text-green-600' };
    }
  };

  const netStatus = getNetCalorieStatus(metrics.netCalories.status);

  return (
    <Card className="nutrition-card-calories nutrition-card-hover animate-fade-in">
      <CardContent className="p-4 space-y-4">
        {/* Main calorie display */}
        <div className="text-center">
          <div className="nutrition-value text-[hsl(var(--nutrition-calories))]">
            {Math.round(metrics.calories.current)}<span className="text-lg text-muted-foreground">/{metrics.calories.goal}</span>
          </div>
          <div className="nutrition-metric">calories today</div>
          <div className="nutrition-progress nutrition-progress-calories mt-2">
            <div 
              className="nutrition-progress-fill" 
              style={{ width: `${metrics.calories.progress}%` }}
            />
          </div>
        </div>

        {/* Net calories */}
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <span className="text-sm font-medium">Net Calories</span>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-mono ${netStatus.color}`}>
              {metrics.netCalories.value > 0 ? '+' : ''}{metrics.netCalories.value}
            </span>
            <Badge variant={netStatus.variant} className="text-xs">
              {netStatus.label}
            </Badge>
          </div>
        </div>

        {/* Compact macros */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="space-y-1">
            <div className="text-sm font-medium text-[hsl(var(--nutrition-protein))]">
              {Math.round(metrics.protein.current)}g
            </div>
            <div className="nutrition-label">Protein</div>
            <div className="nutrition-progress nutrition-progress-protein">
              <div 
                className="nutrition-progress-fill" 
                style={{ width: `${metrics.protein.progress}%` }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-[hsl(var(--nutrition-carbs))]">
              {Math.round(metrics.carbs.current)}g
            </div>
            <div className="nutrition-label">Carbs</div>
            <div className="nutrition-progress nutrition-progress-carbs">
              <div 
                className="nutrition-progress-fill" 
                style={{ width: `${metrics.carbs.progress}%` }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-[hsl(var(--nutrition-fats))]">
              {Math.round(metrics.fats.current)}g
            </div>
            <div className="nutrition-label">Fats</div>
            <div className="nutrition-progress nutrition-progress-fats">
              <div 
                className="nutrition-progress-fill" 
                style={{ width: `${metrics.fats.progress}%` }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}